import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'
import { AI_MODELS_WITH_DEFAULT } from '../constants/aiModels'

type ChatPrompt = Database['public']['Tables']['chat_prompts']['Row']
type ChatPromptInsert = Database['public']['Tables']['chat_prompts']['Insert']
type ChatPromptUpdate = Database['public']['Tables']['chat_prompts']['Update']

export interface ChatPromptWithVideo extends ChatPrompt {
  video: {
    id: string
    title: string
    category: string
    category_id: string | null
    school_id: string | null
  } | null // Allow null for generic prompts
}


export class ChatPromptService {
  static async getAllPrompts(): Promise<ChatPromptWithVideo[]> {
    const { data, error } = await supabase
      .from('chat_prompts')
      .select(`
        *,
        video:videos!video_id(id, title, category, category_id, school_id)
      `)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching chat prompts:', error)
      throw new Error('みらいワークの取得に失敗しました')
    }

    // Hide prompts assigned to school-specific videos; generic prompts (no
    // video) and prompts for global videos are kept.
    return (data as ChatPromptWithVideo[]).filter(
      prompt => !prompt.video?.school_id
    )
  }

  static async getGenericChatPrompt(): Promise<ChatPromptWithVideo | null> {
    const { data, error } = await supabase
      .from('chat_prompts')
      .select(`
        *,
        video:videos!video_id(id, title, category, category_id)
      `)
      .eq('is_generic', true)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) {
      console.error('Error fetching generic chat prompt:', error)
      throw new Error('汎用チャットの取得に失敗しました')
    }

    return data as ChatPromptWithVideo | null
  }


  static async getPromptById(id: string): Promise<ChatPromptWithVideo | null> {
    const { data, error } = await supabase
      .from('chat_prompts')
      .select(`
        *,
        video:videos!video_id(id, title, category, category_id)
      `)
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        return null
      }
      console.error('Error fetching chat prompt:', error)
      throw new Error('みらいワークの取得に失敗しました')
    }

    return data as ChatPromptWithVideo
  }

  static async getPromptByVideoId(videoId: string): Promise<ChatPromptWithVideo | null> {
    const { data, error } = await supabase
      .from('chat_prompts')
      .select(`
        *,
        video:videos!video_id(id, title, category, category_id)
      `)
      .eq('video_id', videoId)
      .maybeSingle()

    if (error) {
      console.error('Error fetching chat prompt by video:', error)
      throw new Error('みらいワークの取得に失敗しました')
    }

    return data as ChatPromptWithVideo | null
  }


  static async createPrompt(promptData: ChatPromptInsert): Promise<ChatPrompt> {
    // Check if trying to create a generic prompt when one already exists
    if ((promptData as any).is_generic) {
      const existingGeneric = await this.getGenericChatPrompt()
      if (existingGeneric) {
        throw new Error('汎用チャットプロンプトは1つまでしか作成できません。既存のプロンプトを編集してください。')
      }
    }

    const { data, error } = await supabase
      .from('chat_prompts')
      .insert(promptData)
      .select()
      .single()

    if (error) {
      console.error('Error creating chat prompt:', error)
      throw new Error('みらいワークの作成に失敗しました')
    }

    return data
  }

  static async updatePrompt(id: string, updates: ChatPromptUpdate): Promise<ChatPrompt> {
    // Check if trying to make this prompt generic when another generic already exists
    if ((updates as any).is_generic) {
      const existingGeneric = await this.getGenericChatPrompt()
      if (existingGeneric && existingGeneric.id !== id) {
        throw new Error('汎用チャットプロンプトは1つまでしか作成できません。既存の汎用プロンプトを無効にしてから設定してください。')
      }
    }

    const { data, error } = await supabase
      .from('chat_prompts')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating chat prompt:', error)
      throw new Error('みらいワークの更新に失敗しました')
    }

    return data
  }

  static async deletePrompt(id: string): Promise<void> {
    const { error } = await supabase
      .from('chat_prompts')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting chat prompt:', error)
      throw new Error('みらいワークの削除に失敗しました')
    }
  }

  static async toggleActive(id: string): Promise<ChatPrompt> {
    // First get the current prompt to toggle its status
    const { data: currentPrompt, error: fetchError } = await supabase
      .from('chat_prompts')
      .select('is_active')
      .eq('id', id)
      .single()

    if (fetchError) {
      console.error('Error fetching current prompt status:', fetchError)
      throw new Error('みらいワークの現在の状態取得に失敗しました')
    }

    const { data, error } = await supabase
      .from('chat_prompts')
      .update({ is_active: !currentPrompt.is_active })
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error toggling prompt active status:', error)
      throw new Error('みらいワークの状態変更に失敗しました')
    }

    return data
  }

  static getAvailableModels(): Array<{ value: string; label: string }> {
    return AI_MODELS_WITH_DEFAULT
  }
}

export type { ChatPrompt, ChatPromptInsert, ChatPromptUpdate }